import { PAGE_SIZE_OPTIONS, PAYMENT_PROMISE_STATUSES, PAYMENT_PROMISE_STATUS_LABELS, type PaymentPromiseListQuery } from '@chukua/common';
import { Pagination, Select } from 'antd';
import { HandCoins, SearchX } from 'lucide-react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { ListSearch } from '../../../../../shared/components/ListSearch';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import type { PaymentPromiseView } from '../payment-promises.api';
import { PromiseCard } from './PromiseCard';

interface PaymentPromisesListProps {
  items: PaymentPromiseView[];
  total: number;
  list: ListQueryControls<PaymentPromiseListQuery>;
  isRefreshing: boolean;
  onSupersede?: (promise: PaymentPromiseView) => void;
}

/** Search by invoice number, a status filter and the server-paged promises; the old ones stay up, dimmed, while the next page loads. */
export function PaymentPromisesList({ items, total, list, isRefreshing, onSupersede }: PaymentPromisesListProps) {
  if (total === 0 && !list.isFiltered) {
    return (
      <EmptyState
        icon={HandCoins}
        title="No promises to pay yet"
        description="When a customer promises to pay by a date, record it from the follow-up on the Finance tab. It appears here."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-3">
        <ListSearch value={list.query.q} placeholder="Search invoice number" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
        <Select
          mode="multiple"
          allowClear
          className="min-w-56"
          placeholder="Status"
          optionFilterProp="label"
          value={list.query.status ?? []}
          options={PAYMENT_PROMISE_STATUSES.map((status) => ({ value: status, label: PAYMENT_PROMISE_STATUS_LABELS[status] }))}
          onChange={(status) => list.setQuery({ status })}
        />
      </div>
      {total === 0 ? (
        <EmptyState icon={SearchX} title="No matching promises" description="No promises match." />
      ) : (
        <ol className={`m-0 flex list-none flex-col gap-3 p-0 ${isRefreshing ? 'opacity-60 transition-opacity' : ''}`} aria-busy={isRefreshing}>
          {items.map((promise) => (
            <PromiseCard key={promise.id} promise={promise} onSupersede={onSupersede} />
          ))}
        </ol>
      )}
      <Pagination
        align="end"
        current={list.query.page}
        pageSize={list.query.pageSize}
        total={total}
        pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
        showSizeChanger
        showTotal={(count) => `${count} total`}
        onChange={(page, pageSize) => list.setQuery({ page, pageSize })}
      />
    </div>
  );
}
